import React, { useEffect, useRef, useState } from "react";
import axios from "axios";

const tabs = [
  ["pending", "Pending"],
  ["queued", "Queue"],
  ["played", "Played"],
  ["declined", "Declined"],
];

const actions = {
  pending: [
    ["queued", "Add to queue"],
    ["declined", "Decline"],
  ],
  queued: [
    ["played", "Mark played"],
    ["declined", "Decline"],
  ],
};

function AddSongModal({
  open,
  onClose,
  onSubmit,
  title,
  setTitle,
  artist,
  setArtist,
  busy,
  error,
}) {
  const dialogRef = useRef(null);

  useEffect(() => {
    const dialog = dialogRef.current;

    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  return (
    <dialog
      ref={dialogRef}
      className="ba-song-dialog"
      aria-labelledby="add-song-heading"
      onCancel={(event) => {
        event.preventDefault();
        if (!busy) onClose();
      }}
      onClick={(event) => {
        if (event.target === event.currentTarget && !busy) {
          onClose();
        }
      }}
    >
      <div className="ba-song-dialog-body">
        <div className="ba-song-dialog-header">
          <div>
            <h3 id="add-song-heading">Add a song</h3>
            <p>Add a request for this branch.</p>
          </div>

          <button
            type="button"
            className="ba-dialog-close"
            aria-label="Close add song"
            disabled={busy}
            onClick={onClose}
          >
            ×
          </button>
        </div>

        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}

        <form onSubmit={onSubmit}>
          <fieldset disabled={busy}>
            <label htmlFor="music-title">Song title</label>
            <input
              id="music-title"
              className="form-control"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder="Enter song title"
              maxLength={200}
              autoFocus
              required
            />

            <label htmlFor="music-artist" className="ba-song-artist-label">
              Artist <span>(optional)</span>
            </label>
            <input
              id="music-artist"
              className="form-control"
              value={artist}
              onChange={(event) => setArtist(event.target.value)}
              placeholder="Enter artist name"
              maxLength={200}
            />

            <div className="ba-dialog-actions">
              <button
                type="button"
                className="btn btn-outline-light"
                onClick={onClose}
                disabled={busy}
              >
                Cancel
              </button>

              <button
                className="btn btn-primary"
                type="submit"
                disabled={busy || !title.trim()}
              >
                {busy ? "Adding…" : "Add song"}
              </button>
            </div>
          </fieldset>
        </form>
      </div>
    </dialog>
  );
}

function headers() {
  return {
    Authorization: localStorage.getItem("jwtToken") || "",
  };
}

export default function AdminMusic({ circleId }) {
  const [status, setStatus] = useState("pending");
  const [requests, setRequests] = useState([]);
  const [title, setTitle] = useState("");
  const [artist, setArtist] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [revision, setRevision] = useState(0);
  const [musicEnabled, setMusicEnabled] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [showAddSong, setShowAddSong] = useState(false);

  const base =
    process.env.REACT_APP_API_URL + `circles/admin/${circleId}/music`;

  useEffect(() => {
    let cancelled = false;

    setLoading(true);
    setError("");
    setRequests([]);

    axios
      .get(base, {
        headers: headers(),
        params: { status },
      })
      .then(({ data }) => {
        if (!cancelled) {
          setRequests(data.requests);
          setMusicEnabled(data.musicRequestsEnabled === true);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.response?.data?.message || "Unable to load songs.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [base, status, revision]);

  useEffect(() => {
    if (!message) return;

    const timeout = setTimeout(() => setMessage(""), 3000);

    return () => clearTimeout(timeout);
  }, [message]);

  const addSong = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    setMessage("");

    try {
      await axios.post(
        base,
        { title: title.trim(), artist: artist.trim() },
        { headers: headers() },
      );

      setTitle("");
      setArtist("");
      setStatus("pending");
      setRevision((value) => value + 1);
      setMessage("Song added to Pending.");
      setShowAddSong(false);
    } catch (err) {
      setError(err.response?.data?.message || "Unable to add song.");
    } finally {
      setBusy(false);
    }
  };

  const changeStatus = async (requestId, nextStatus) => {
    setBusy(true);
    setError("");
    setMessage("");

    try {
      await axios.patch(
        `${base}/${requestId}/status`,
        { status: nextStatus },
        { headers: headers() },
      );

      setRevision((value) => value + 1);
    } catch (err) {
      setError(err.response?.data?.message || "Unable to update song.");
    } finally {
      setBusy(false);
    }
  };

  const toggleMusicRequests = async () => {
    setSavingSettings(true);
    setError("");
    setMessage("");

    try {
      const { data } = await axios.patch(
        `${base}/settings`,
        { musicRequestsEnabled: !musicEnabled },
        { headers: headers() },
      );

      setMusicEnabled(data.musicRequestsEnabled);
      setMessage(
        data.musicRequestsEnabled
          ? "Song requests enabled."
          : "Song requests disabled.",
      );
    } catch (err) {
      setError(
        err.response?.data?.message || "Unable to update music settings.",
      );
    } finally {
      setSavingSettings(false);
    }
  };

  return (
    <section className="ba-panel ba-music">
      <div className="ba-music-header">
        <div>
          <h3>Music requests</h3>
          <p>Manage requests and shape your branch’s soundtrack.</p>
        </div>

        <div className="ba-music-tools">
          <div className="ba-music-setting">
            <span>Member requests</span>

            <button
              type="button"
              role="switch"
              aria-label="Allow members to request songs"
              aria-checked={musicEnabled}
              className={
                musicEnabled ? "btn btn-success" : "btn btn-outline-light"
              }
              disabled={loading || savingSettings || busy}
              onClick={toggleMusicRequests}
            >
              {savingSettings
                ? "Saving…"
                : musicEnabled
                  ? "Enabled"
                  : "Disabled"}
            </button>
          </div>

          <button
            type="button"
            className="ba-add-song-link"
            disabled={busy || savingSettings}
            onClick={() => {
              setError("");
              setMessage("");
              setShowAddSong(true);
            }}
          >
            + Add song
          </button>
        </div>
      </div>

      {error && !showAddSong && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {message && (
        <div className="alert alert-success" role="status">
          {message}
        </div>
      )}

      <AddSongModal
        open={showAddSong}
        onClose={() => setShowAddSong(false)}
        onSubmit={addSong}
        title={title}
        setTitle={setTitle}
        artist={artist}
        setArtist={setArtist}
        busy={busy}
        error={error}
      />

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 10,
          marginTop: 30,
          marginBottom: 20,
        }}
      >
        {tabs.map(([value, label]) => (
          <button
            key={value}
            type="button"
            className={
              status === value ? "btn btn-primary" : "btn btn-outline-light"
            }
            aria-pressed={status === value}
            disabled={busy}
            onClick={() => {
              setStatus(value);
              setMessage("");
            }}
          >
            {label}
          </button>
        ))}

        <button
          type="button"
          className="btn btn-outline-light"
          disabled={loading || busy}
          onClick={() => setRevision((value) => value + 1)}
        >
          Refresh
        </button>
      </div>

      {loading ? (
        <p role="status">Loading songs…</p>
      ) : error ? null : requests.length === 0 ? (
        <div className="ba-music-empty">
          <span aria-hidden="true">♫</span>
          <strong>No songs here yet</strong>
          <p>Requests will appear here when they’re added.</p>
        </div>
      ) : (
        <div className="ba-song-list">
          <div className="ba-song-list-heading">
            <span>Song / Artist</span>
            <span>Requested</span>
            <span className="ba-song-actions-heading">Actions</span>
          </div>

          {requests.map((request, index) => (
            <div className="ba-song-row" key={request._id}>
              <div className="ba-song-identity">
                <span className="ba-song-number" aria-hidden="true">
                  {status === "queued" ? index + 1 : "♫"}
                </span>

                <div className="ba-song-details">
                  <strong dir="auto">{request.title}</strong>
                  <span dir="auto">
                    {request.artist || "Artist not specified"}
                  </span>
                </div>
              </div>

              <time
                className="ba-song-date"
                dateTime={request.createdAt}
                title={new Date(request.createdAt).toLocaleString()}
              >
                {new Date(request.createdAt).toLocaleDateString(undefined, {
                  month: "short",
                  day: "numeric",
                })}
                <span>
                  {new Date(request.createdAt).toLocaleTimeString(undefined, {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </span>
              </time>

              <div className="ba-song-actions">
                <a
                  href={`https://www.youtube.com/results?search_query=${encodeURIComponent(
                    [request.title, request.artist].filter(Boolean).join(" "),
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn ba-youtube-button"
                  aria-label={`Search YouTube for ${request.title}${
                    request.artist ? ` by ${request.artist}` : ""
                  } (opens in a new tab)`}
                >
                  YouTube ↗
                </a>
                {(actions[status] || []).map(([nextStatus, label]) => (
                  <button
                    key={nextStatus}
                    type="button"
                    className={
                      nextStatus === "declined"
                        ? "btn btn-outline-danger"
                        : "btn btn-primary"
                    }
                    disabled={busy || savingSettings}
                    onClick={() => changeStatus(request._id, nextStatus)}
                    aria-label={`${label}: ${request.title}`}
                  >
                    {label}
                  </button>
                ))}

                {!actions[status] && (
                  <span className={`ba-song-status ba-song-status-${status}`}>
                    {status === "played" ? "Played" : "Declined"}
                  </span>
                )}
              </div>
            </div>
          ))}

          <div className="ba-song-list-footer">
            {requests.length} {requests.length === 1 ? "song" : "songs"} shown
            {requests.length === 100 ? " · Display limited to 100" : ""}
          </div>
        </div>
      )}
    </section>
  );
}
